/**
 * Global app shell: PowerSync lifecycle + the "active" property (per the
 * BetterAuth session's `activeOrganizationId`).
 *
 * Read-side filtering is per-view, via `useScope`. The only piece of property
 * state that is genuinely global is "which property are we writing to right
 * now" — i.e. BetterAuth's active organization id — which mutation forms set
 * when their Property field changes. This provider exposes that as
 * `currentProperty` for in-context fallbacks (the staff-DM draft flow being
 * the load-bearing one).
 *
 * - `currentStaff` — the logged-in staff member
 * - `currentProperty` / `propertySlug` — the session's active property
 *   (or the first member property as a fallback for fresh installs)
 * - `tenantStreamsReady` — true once the property-scoped stream carrying
 *   conversational data has first-synced for every accessible property
 */

import type { SyncStatus } from "@powersync/web";
import * as Sentry from "@sentry/capacitor";
import { useLiveQuery as useTanstackLiveQuery } from "@tanstack/react-db";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import type { Property } from "@/lib/collections/properties";
import type { Staff } from "@/lib/collections/staff";
import { staffCollection } from "@/lib/collections/staff";
import {
  readPropertyCatalogSynced,
  resolvePrimaryProperty,
} from "@/lib/context/primary-property";
import {
  ensureReplicaBelongsTo,
  isReplicaReloadPending,
  markReplicaRendered,
  mustReloadBeforeRendering,
  type ReplicaCheck,
  readReplicaOwner,
} from "@/lib/device-replica";
import { useAllProperties } from "@/lib/hooks/use-all-properties";
import { useAppSession } from "@/lib/offline-session";
import {
  connectPowerSync,
  disconnectPowerSync,
  powerSyncDb,
} from "@/lib/powersync";
import { watchStuckUploads } from "@/lib/powersync-pending";
import { ensureSelectedPropertiesStreams } from "@/lib/powersync-tenant-subscriptions";
import { isFirstReport } from "@/lib/report-distinct";

interface AppContextValue {
  /**
   * Raw session `activeOrganizationId` (a property id), or null before the
   * session resolves. Unlike `currentProperty` this has no "first property"
   * fallback, so it's the precise signal for "is the session pointed at the
   * property we expect" — property-scoped REST endpoints resolve their
   * tenant from it server-side.
   */
  activeOrgId: string | null;
  /**
   * The session's active property, or the first accessible one when the
   * session names none. Null while the session names a property whose row
   * hasn't synced down yet — never another property standing in for it
   * (see resolvePrimaryProperty).
   */
  currentProperty: Property | null;
  currentStaff: Staff | null;
  hasSynced: boolean;
  /** Slug of the first selected property. */
  propertySlug: string | undefined;
  /**
   * True while the sync stream is established. Derived from PowerSync's
   * own `SyncStatus.connected` rather than from `connect()` resolving:
   * `connect()` resolves (never rejects) even when the sync service is
   * unreachable, because the SDK retries the stream internally forever.
   */
  syncConnected: boolean;
  /**
   * Message of the last sync download error, or null when the stream is
   * healthy (or hasn't failed yet). Set when the sync service can't be
   * reached; cleared by the SDK once a connection attempt succeeds.
   */
  syncError: string | null;
  /**
   * True once PowerSync has read from the local database whether this device
   * ever synced; until then `hasSynced` is false for every device.
   */
  syncStatusLoaded: boolean;
  /**
   * True once `selected_property` — the stream carrying guest/staff/AI
   * chats, guests and suggestions — has first-synced for every accessible
   * property. Distinct from `hasSynced`, which latches as soon as the
   * auto-subscribed catalog streams land, necessarily earlier: the property
   * ids `selected_property` needs come from the catalog itself, so it is
   * always a second, later download. Screens fed by it must gate on this,
   * not on `hasSynced`, or they render their real empty state in the gap.
   */
  tenantStreamsReady: boolean;
  /**
   * True while the oldest change on this device has waited past
   * `STUCK_UPLOAD_MS` of checks with the sync stream up: the server keeps
   * refusing or erroring on it.
   */
  uploadsStuck: boolean;
}

const AppContext = createContext<AppContextValue | null>(null);

/** The first gap before checking the replica again after an error. */
const REPLICA_CHECK_RETRY_MS = 3000;

/** The longest gap between checks while a replica keeps failing. */
const REPLICA_CHECK_MAX_RETRY_MS = 60_000;

/**
 * Mounts the provider only once the on-device replica is known to belong to
 * the signed-in account (device-replica.ts). Nothing below it renders before
 * then: a replica left by another account is cleared first, and the page
 * reloaded when the clear came too late for what is already in memory.
 *
 * The owner check is synchronous for the account that owns the replica, so an
 * offline cold start renders local data on the first frame. Keyed by
 * user id: a different account remounts everything under a fresh check.
 */
export function AppProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useAppSession();
  const userId = session?.user.id ?? null;
  const [verifiedOwner, setVerifiedOwner] = useState<string | null>(() => {
    const cannotTrustMemory =
      isReplicaReloadPending() ||
      (userId !== null && mustReloadBeforeRendering(userId));
    return cannotTrustMemory ? null : readReplicaOwner();
  });
  const [attempt, setAttempt] = useState(0);
  // Consecutive failed checks for the current account, for the retry gap. A
  // ref, since counting must not itself re-run the check.
  const failures = useRef({ count: 0, userId: null as string | null });
  const retryNow = useCallback(() => setAttempt((n) => n + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the retry trigger; bumping it is what runs the check again.
  useEffect(() => {
    if (!userId) {
      return;
    }
    if (failures.current.userId !== userId) {
      failures.current = { count: 0, userId };
    }
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const verify = async () => {
      let check: ReplicaCheck;
      try {
        check = await ensureReplicaBelongsTo(userId);
      } catch (err) {
        // Fail closed, and never act destructively on a failure: nothing
        // renders until the check passes, and it is retried with growing
        // gaps, since the usual causes (a database still opening, or locked)
        // pass on their own. Each distinct failure reaches Sentry.
        reportCheckFailure(err);
        if (!cancelled) {
          failures.current.count += 1;
          retryTimer = setTimeout(
            retryNow,
            replicaRetryDelay(failures.current.count)
          );
        }
        return;
      }
      if (!cancelled && check === "ready") {
        failures.current.count = 0;
        setVerifiedOwner(userId);
      }
    };
    void verify();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [userId, attempt, retryNow]);

  // A check that has asked for a reload closes the gate at once, whatever
  // was verified before, so nothing renders while the page unloads.
  const isReplicaVerified =
    userId !== null && verifiedOwner === userId && !isReplicaReloadPending();
  if (!isReplicaVerified) {
    return <LoadingScreen />;
  }
  markReplicaRendered(userId);
  return <AppStateProvider key={userId}>{children}</AppStateProvider>;
}

/** Retry gaps: 3s, 6s, 12s… capped at a minute. */
function replicaRetryDelay(failureCount: number): number {
  const doublings = Math.max(failureCount - 1, 0);
  return Math.min(
    REPLICA_CHECK_RETRY_MS * 2 ** doublings,
    REPLICA_CHECK_MAX_RETRY_MS
  );
}

/**
 * One Sentry event per distinct failure per page: the check retries every few
 * seconds, and the first error is often a transient lock while the one that
 * keeps a device locked out comes later.
 */
const reportedCheckFailures = new Set<string>();

function reportCheckFailure(err: unknown): void {
  console.error("[replica] could not verify the local replica:", err);
  const message =
    err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  if (isFirstReport(reportedCheckFailures, message)) {
    Sentry.captureException(err);
  }
}

function AppStateProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useAppSession();
  const allProperties = useAllProperties();

  const [syncConnected, setSyncConnected] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [hasSynced, setHasSynced] = useState(false);
  const [syncStatusLoaded, setSyncStatusLoaded] = useState(false);
  const [propertyCatalogSynced, setPropertyCatalogSynced] = useState(false);

  useEffect(() => {
    if (!session?.user.id) {
      console.log("[powersync] effect: no session yet, skipping");
      return;
    }
    console.log("[powersync] effect: starting for user", session.user.id);

    let cancelled = false;

    // hasSynced latches true and never goes back: PowerSync restores it
    // from the local database at init (resolveOfflineSyncStatus), so a
    // previously-synced device reports true with no network involved.
    // connected/downloadError track the live stream so the UI can tell
    // "rendering local data, sync paused" apart from "all good".
    const applyStatus = (status: SyncStatus) => {
      if (cancelled) {
        return;
      }
      if (status.hasSynced === true) {
        setHasSynced(true);
      }
      if (status.hasSynced !== undefined) {
        setSyncStatusLoaded(true);
      }
      // Latched like hasSynced: the catalog can't un-sync, and a status
      // update that momentarily reports no stream table (a reconnect while
      // the DB reopens) must not flip the active property back to null.
      if (readPropertyCatalogSynced(status)) {
        setPropertyCatalogSynced(true);
      }
      setSyncConnected(status.connected === true);
      const { downloadError } = status;
      // Store the message (not the Error): retries produce a fresh Error
      // instance with the same message every few seconds, and identical
      // strings let React's setState bail out instead of re-rendering.
      setSyncError(
        downloadError ? (downloadError.message ?? String(downloadError)) : null
      );
    };

    try {
      applyStatus(powerSyncDb.currentStatus);
    } catch (e) {
      console.warn("[powersync] could not read initial status:", e);
    }

    const removeListener = powerSyncDb.registerListener({
      statusChanged: applyStatus,
    });

    // connect() resolving does NOT mean the service is reachable: the SDK
    // sets up the stream and keeps retrying internally, reporting failures
    // only via statusChanged (handled above). The catch is for genuine
    // setup errors thrown before the stream starts.
    void connectPowerSync().catch((err) => {
      console.error("[powersync] connection failed:", err);
      if (!cancelled) {
        setSyncError(err instanceof Error ? err.message : String(err));
      }
    });

    return () => {
      cancelled = true;
      removeListener();
      void disconnectPowerSync();
    };
  }, [session?.user.id]);

  const [uploadsStuck, setUploadsStuck] = useState(false);
  useEffect(() => watchStuckUploads(setUploadsStuck), []);

  const { data: staff = [] } = useTanstackLiveQuery(
    (q) => q.from({ s: staffCollection }).select(({ s }) => s),
    []
  );

  const currentStaff = useMemo(() => {
    if (!session?.user.id || staff.length === 0) {
      return null;
    }
    return staff.find((s) => s.id === session.user.id) ?? null;
  }, [session, staff]);

  // Resolve the active property from the BetterAuth session, falling back
  // to the first accessible property only once that fallback is safe (see
  // resolvePrimaryProperty). Mutation forms call
  // `authClient.organization.setActive({ organizationId })` to flip this.
  const activeOrgId = session?.session.activeOrganizationId;
  const primaryProperty = useMemo<Property | null>(
    () =>
      resolvePrimaryProperty({
        activeOrgId: activeOrgId ?? null,
        allProperties,
        catalogSynced: propertyCatalogSynced,
      }),
    [activeOrgId, allProperties, propertyCatalogSynced]
  );

  // Subscribe PowerSync to every property the user has access to. The
  // bootstrap stream supplies the `properties` catalog (auto-subscribed),
  // so we can derive the subscription set from `allProperties` directly —
  // no separate membership query needed. `ensureSelectedPropertiesStreams`
  // diffs against the previous subscription set, so re-running this effect
  // on every property change is cheap (only new ids subscribe).
  //
  // Important: do NOT early-return when `allProperties` is empty. The diff
  // is bidirectional — calling with `[]` is what tells PowerSync to drop
  // any previously-subscribed streams. Skipping the call on empty leaves
  // stale subscriptions in place after the user is removed from every
  // property (or signs out), which wastes bandwidth and risks delivering
  // rows the user no longer has access to until the next non-empty call.
  //
  // Keyed on the id list rather than the array identity: the live query
  // re-emits a fresh array on every unrelated `properties` write, and each
  // re-run would otherwise reset `syncedStreamsKey` below and re-flash the
  // placeholder on screens that are already showing data.
  const propertyIdsKey = allProperties
    .map((p) => p.id)
    .sort((a, b) => a.localeCompare(b))
    .join(",");
  const [syncedStreamsKey, setSyncedStreamsKey] = useState<string | null>(null);

  // Which property is active decides the TTL its stream is subscribed with,
  // so the subscriptions need to hear about it. Re-running on a change is
  // cheap — it never re-subscribes a live stream — and safe, because
  // `syncedStreamsKey` only ever advances, so readiness never flickers.
  const primaryPropertyId = primaryProperty?.id ?? null;

  useEffect(() => {
    const ids = propertyIdsKey === "" ? [] : propertyIdsKey.split(",");
    let cancelled = false;

    ensureSelectedPropertiesStreams(ids, primaryPropertyId)
      .catch((err) => {
        console.error("[app] property stream subscribe failed:", err);
      })
      .finally(() => {
        // Ready on settle, not only on success: a subscribe that can never
        // complete would otherwise pin every property-scoped screen to a
        // skeleton forever, which reads worse than an empty list. A stream
        // that is merely slow (rather than broken) still resolves through
        // `waitForFirstSync`, and genuine failures surface as `syncError`.
        if (!cancelled) {
          setSyncedStreamsKey(propertyIdsKey);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [propertyIdsKey, primaryPropertyId]);

  // An empty id list never counts as ready, however settled it looks.
  //
  // `ensureSelectedPropertiesStreams([])` subscribes to nothing, so it resolves
  // the moment it is called and records `syncedStreamsKey = ""`. That satisfies
  // the equality below while no conversation stream has been requested at all,
  // and every cold start passes through exactly that state: the catalog stream
  // first-syncs (flipping `propertyCatalogSynced`) a tick before the
  // `allProperties` live query re-emits its rows, so `propertyIdsKey` is still
  // empty when the flag flips. Property-scoped lists then read "synced, zero
  // rows" and flash their real empty state — "No guest chats found" on a host's
  // very first launch — before the rows land.
  //
  // Requiring a non-empty id list costs nothing for a reader who genuinely has
  // no property: `useHasNoProperty` replaces the whole tab with
  // `PropertyEmptyState` before any of these lists mount, so a `false` here is
  // never read. It also needs no settle window — the condition is the
  // subscription set itself, not a guess at how long hydration takes.
  const tenantStreamsReady =
    propertyCatalogSynced &&
    propertyIdsKey !== "" &&
    syncedStreamsKey === propertyIdsKey;

  const value: AppContextValue = {
    activeOrgId: activeOrgId ?? null,
    currentProperty: primaryProperty,
    currentStaff,
    hasSynced,
    propertySlug: primaryProperty?.slug,
    syncConnected,
    syncError,
    syncStatusLoaded,
    tenantStreamsReady,
    uploadsStuck,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
}
